/* fs-subscription-tiers.js — portable Subscribe & Save tier grid
   Pairs with snippets/fs-subscription-tiers.liquid + fs-subscription-tiers.css

   WHAT IT DOES
   1. Applies the preselected tier to the real form inputs ON LOAD (the original
      only did this on click, so a shopper who landed and hit Add to Cart got a
      one-time single unit while a subscription tier looked selected).
   2. Switches selling_plan / variant / quantity when a tier is picked.
   3. Repaints price, ATC price and save badge from the active row.
   4. Hides the quantity stepper + bulk-pack ladder while a subscription tier is
      selected (a 12-pack x stepper = 24 units; bulk ladder double-discounts).
   5. Blocks adding the SAME plan twice on quantity-upsell products.
   6. (qty mode) Follows variant-option switches (e.g. Bottle / Pouch): repaints
      every row's price, per-day, unit price, compare-at strike and save line
      from the selected variant, and re-points each row at that variant so
      clicking a row can never put the previously selected variant back.

   HOOKS IT LOOKS FOR (all optional, anywhere on the page):
     [data-fst-price]      -> gets the active row's price
     [data-fst-atc-price]  -> gets the active row's price
     [data-fst-badge]      -> shown/hidden; its [data-fst-badge-pct] gets max %
     [data-fst-qty-box]    -> hidden while a subscription tier is active
     [data-fst-bulk-box]   -> hidden while a subscription tier is active
     [data-fst-variants]   -> (qty mode, rendered by the snippet) per-variant JSON
*/
(function () {
  'use strict';

  function init(root) {
    if (root.__fstInit) return;
    root.__fstInit = true;

    var mode = root.getAttribute('data-fst-mode') || 'toggle';
    var productId = root.getAttribute('data-fst-product-id') || '';
    var form = root.closest('form[action*="/cart/add"]') ||
               document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var planInput = root.querySelector('[data-fst-plan-input]');
    var variantInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-fst-row]'));
    if (!rows.length) return;

    var freqWrap = root.querySelector('[data-fst-freq]');
    var freqSelect = root.querySelector('[data-fst-freq-select]');

    // page-level paint targets. `:not([data-fst-row])` matters: every row button ALSO
    // carries its own data-fst-price (the price applyRow() reads off the clicked row) —
    // without excluding rows here, this selector matches them too, and the forEach below
    // wipes each row's whole rendered inside (label, badge, struck price) down to bare
    // price text the moment the page loads.
    var priceEls = document.querySelectorAll('[data-fst-price]:not([data-fst-row])');
    var atcEls = document.querySelectorAll('[data-fst-atc-price]:not([data-fst-row])');
    var qtyBox = document.querySelector('[data-fst-qty-box]');
    var bulkBox = document.querySelector('[data-fst-bulk-box]');

    // ---- save badge: one fixed figure, the best deal on the product ---------
    // Deliberately NOT repainted per selection — a badge that shrank as the
    // shopper looked at cheaper tiers would read as a loss.
    (function paintBadge() {
      var badge = document.querySelector('[data-fst-badge]');
      if (!badge) return;
      var pct = parseInt(root.getAttribute('data-fst-max-pct'), 10) || 0;
      var pctEl = badge.querySelector('[data-fst-badge-pct]');
      if (pct > 0) {
        if (pctEl) pctEl.textContent = pct;
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    })();

    function activeFreqPlanId() {
      return freqSelect ? (freqSelect.value || '') : '';
    }

    // ---- the one place that writes form state -------------------------------
    function applyRow(row, isInitial) {
      rows.forEach(function (r) {
        var on = r === row;
        r.classList.toggle('is-active', on);
        r.setAttribute('aria-checked', on ? 'true' : 'false');
      });

      var isSub = row.getAttribute('data-fst-purchase') === 'subscribe';
      var rowPlan = row.getAttribute('data-fst-plan-id') || '';

      // Toggle mode reads the plan off the frequency <select>; tier modes carry
      // their own plan id on the row.
      if (planInput) planInput.value = isSub ? (rowPlan || activeFreqPlanId()) : '';

      var vid = row.getAttribute('data-fst-variant-id');
      if (vid && variantInput) variantInput.value = vid;

      var q = row.getAttribute('data-fst-qty') || '1';
      if (qtyInput) qtyInput.value = q;

      if (freqWrap) freqWrap.hidden = !isSub;
      if (qtyBox) qtyBox.hidden = isSub;
      if (bulkBox) bulkBox.hidden = isSub;

      var price = row.getAttribute('data-fst-price');
      if (price) {
        priceEls.forEach(function (el) { el.textContent = price; });
        atcEls.forEach(function (el) { el.textContent = price; });
      }

      root.dispatchEvent(new CustomEvent('fst:change', {
        bubbles: true,
        detail: {
          planId: planInput ? planInput.value : '',
          variantId: vid,
          quantity: parseInt(q, 10),
          price: price,
          isSubscription: isSub,
          initial: !!isInitial
        }
      }));
    }

    rows.forEach(function (row) {
      row.addEventListener('click', function () {
        if (row.hidden) return;
        applyRow(row, false);
      });
    });

    // ---- frequency dropdown (toggle mode) -----------------------------------
    if (freqSelect) {
      freqSelect.addEventListener('change', function () {
        var opt = freqSelect.options[freqSelect.selectedIndex];
        var active = root.querySelector('[data-fst-row].is-active');
        var isSub = active && active.getAttribute('data-fst-purchase') === 'subscribe';
        if (planInput && isSub) planInput.value = freqSelect.value || '';

        var pp = opt ? opt.getAttribute('data-fst-plan-price') : '';
        var ps = opt ? opt.getAttribute('data-fst-plan-save') : '';
        if (pp) {
          // keep the subscribe row's own figure in sync, else it stays stuck on
          // the default plan's price after a frequency change
          var subRow = root.querySelector('[data-fst-row][data-fst-purchase="subscribe"]');
          if (subRow) subRow.setAttribute('data-fst-price', pp);
          var subPriceEl = root.querySelector('[data-fst-sub-price]');
          if (subPriceEl) subPriceEl.textContent = pp;
          if (isSub) {
            priceEls.forEach(function (el) { el.textContent = pp; });
            atcEls.forEach(function (el) { el.textContent = pp; });
          }
        }
        var subSaveEl = root.querySelector('[data-fst-sub-save]');
        if (subSaveEl && ps != null) subSaveEl.textContent = ps;
      });
    }

    // ---- qty mode: follow variant-option switches (Bottle / Pouch) ----------
    // Rows are server-rendered for the variant selected at page load. Without
    // this, every row kept that variant's id, so picking "Pouch" and then
    // clicking a row wrote the Bottle id back into the form.
    var vData = null;
    var vDataEl = root.querySelector('[data-fst-variants]');
    if (mode === 'qty' && vDataEl) {
      try { vData = JSON.parse(vDataEl.textContent); } catch (err) { vData = null; }
    }

    function money(cents) {
      var fmt = (vData && vData.money_format) || '${{amount}}';
      var n = (cents / 100).toFixed(2);
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
        if (key === 'amount_no_decimals') return String(Math.round(cents / 100));
        if (key === 'amount_with_comma_separator') return n.replace('.', ',');
        if (key === 'amount_no_decimals_with_comma_separator') return String(Math.round(cents / 100));
        return n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      });
    }

    function selectedOptions() {
      // Radios named option1, option2 … in the product form (fs-pdp-main).
      var picked = [];
      form.querySelectorAll('input[type="radio"][name^="option"]:checked').forEach(function (r) {
        var pos = parseInt(r.name.replace('option', ''), 10);
        if (pos > 0) picked[pos - 1] = r.value;
      });
      return picked;
    }

    function findVariant() {
      if (!vData || !vData.variants) return null;
      var opts = selectedOptions();
      if (!opts.length) return null;
      for (var i = 0; i < vData.variants.length; i++) {
        var v = vData.variants[i];
        var ok = v.options.every(function (o, idx) { return opts[idx] === undefined || o === opts[idx]; });
        if (ok) return v;
      }
      return null;
    }

    function setText(row, sel, text) {
      var el = row.querySelector(sel);
      if (el) el.textContent = text;
    }

    function repaintRows(v) {
      var days = parseInt(vData.days, 10) || 0;
      rows.forEach(function (row) {
        var isSub = row.getAttribute('data-fst-purchase') === 'subscribe';
        var units = parseInt(row.getAttribute('data-fst-units') || row.getAttribute('data-fst-qty'), 10) || 1;
        var planId = row.getAttribute('data-fst-plan-id') || '';

        var total, was;
        if (isSub) {
          var perUnit = v.plans ? v.plans[planId] : undefined;
          if (perUnit === undefined) { row.hidden = true; return; } // plan not offered on this variant
          total = perUnit * units;
          was = v.price * units;
        } else {
          total = v.price;
          was = (vData.onetime_compare && v.compare_at > v.price) ? v.compare_at : v.price;
        }
        row.hidden = false;

        var pct = 0;
        if (isSub && was > total) pct = Math.round(((was - total) * 100) / was);

        row.setAttribute('data-fst-variant-id', v.id);
        row.setAttribute('data-fst-price', money(total));
        row.setAttribute('data-fst-save-pct', pct);

        setText(row, '.fst__unit', money(Math.floor(total / units)) + ' / ' + (vData.unit || ''));
        if (vData.show_day && days > 0) setText(row, '.fst__day', money(Math.floor(total / (units * days))) + '/day');

        var totals = row.querySelector('.fst__totals');
        if (totals) {
          totals.innerHTML = (was > total ? '<s data-fst-was>' + money(was) + '</s>' : '') +
                             '<strong>' + money(total) + '</strong>';
        }

        // "You save" line — subscribe rows only (one-time shows the strike only)
        var priceWrap = row.querySelector('.fst__price');
        var saved = row.querySelector('.fst__saved');
        if (isSub && was > total) {
          if (!saved && priceWrap) {
            saved = document.createElement('span');
            saved.className = 'fst__saved';
            priceWrap.appendChild(saved);
          }
          if (saved) {
            saved.hidden = false;
            saved.innerHTML = (vData.saved_prefix || 'You save') + ' ' + money(was - total) +
              (pct > 0 ? ' <span class="fst__pct">(' + pct + '% off)</span>' : '');
          }
        } else if (saved) {
          saved.hidden = true;
        }
      });
    }

    if (vData) {
      // Delegated on the form, so it runs AFTER fs-pdp-main's own radio listener
      // (which repaints the headline with the one-time price). Re-applying the
      // active row then puts the correct tier price back on the headline + ATC.
      form.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || t.type !== 'radio' || !/^option\d+$/.test(t.name || '')) return;
        var v = findVariant();
        if (!v) return;
        repaintRows(v);
        var active = root.querySelector('[data-fst-row].is-active');
        if (!active || active.hidden) {
          active = rows.filter(function (r) { return !r.hidden; })[0];
        }
        if (active) applyRow(active, false);
      });
    }

    // ---- FIX: sync the preselected row into the form on load ----------------
    var initial = root.querySelector('[data-fst-row].is-active') || rows[0];
    applyRow(initial, true);

    // ---- duplicate-subscription guard (quantity-upsell products) ------------
    // A qty-upsell tier is a fixed supply ("12 bottles in one plan"). Adding the
    // same plan twice merges into a doubled quantity, silently turning a 12-pack
    // into 24. Block it and point the shopper at the cart instead.
    if (!root.hasAttribute('data-fst-guard')) return;

    var routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cartUrl = routeRoot.replace(/\/$/, '') + '/cart';
    var snapshot = { items: [] };

    function refresh() {
      return fetch(routeRoot.replace(/\/$/, '') + '/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (c) { snapshot = c; })
        .catch(function () {});
    }
    refresh();

    // re-snapshot after any cart change, whichever event the theme fires
    ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function (ev) {
      document.addEventListener(ev, refresh);
    });
    if (typeof window.subscribe === 'function' &&
        typeof window.PUB_SUB_EVENTS !== 'undefined' && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
    }

    function samePlanInCart(planId) {
      if (!planId) return false;
      return (snapshot.items || []).some(function (it) {
        return it.selling_plan_allocation &&
               it.selling_plan_allocation.selling_plan &&
               String(it.selling_plan_allocation.selling_plan.id) === String(planId);
      });
    }

    function toast() {
      var box = document.querySelector('[data-fst-notice]');
      if (!box) {
        box = document.createElement('div');
        box.className = 'fst__notice';
        box.setAttribute('data-fst-notice', '');
        box.setAttribute('role', 'status');
        document.body.appendChild(box);
      }
      box.innerHTML = 'This subscription is already in your cart. ' +
        '<a href="' + cartUrl + '" class="fst__notice-link">View cart</a> or ' +
        '<a href="' + cartUrl + '/checkout" class="fst__notice-link">check out</a>.';
      box.hidden = false;
      clearTimeout(box._t);
      box._t = setTimeout(function () { box.hidden = true; }, 8000);
    }

    // Intercept on the CLICK, document-capture — the earliest point in the
    // propagation path. Most AJAX-cart themes bind their own capture handler on
    // the form component, which runs after document-capture. Decide from the
    // cached snapshot so the decision is synchronous; an async fetch here caused
    // a double submit in testing.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[type="submit"], button[name="add"], [data-fst-atc]');
      if (!btn) return;
      var btnForm = btn.form || btn.closest('form');
      if (btnForm !== form) return;

      var active = root.querySelector('[data-fst-row].is-active');
      if (!active || active.getAttribute('data-fst-purchase') !== 'subscribe') return;
      if (!samePlanInCart(active.getAttribute('data-fst-plan-id'))) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      toast();
    }, true);
  }

  function boot() {
    document.querySelectorAll('[data-fst]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', boot);
})();
